import { useCallback, useEffect, useState } from "react";
import { Api, ApiError, type DriveFiles, type IntegrationState, type Source } from "../api.js";
import { useI18n } from "../i18n.js";
import { pickDriveFiles } from "../picker.js";
import { Badge, Section } from "./ui.js";

const KNOWN = ["not_connected", "needs_reconnect", "held", "limit_reached", "too_soon", "forbidden", "not_enabled", "rate_limited"];
const RESULTS = ["connected", "denied", "missing_scope", "bad_state", "provider_failed", "held", "not_enabled"];

/** What the browser came back with from Google: `#/…?integration=google_drive&result=connected`. */
export function readReturn(hash: string): { provider: string; result: string } | null {
  const q = new URLSearchParams(hash.split("?")[1] ?? "");
  const provider = q.get("integration");
  const result = q.get("result");
  return provider && result ? { provider, result } : null;
}

/**
 * Google Drive in the Train step: connect, pick the files, see each one's state, disconnect (keeping or removing what was read).
 * Shows nothing at all while the connection is switched off on the server (the older "coming soon" row stays then).
 */
export function DrivePanel({ api, agentId, sources, refresh, state, reload }: { api: Api; agentId: string; sources: Source[]; refresh: () => Promise<void> | void; state: IntegrationState; reload: () => Promise<void> }) {
  const { t, lang } = useI18n();
  const [busy, setBusy] = useState<"" | "connect" | "pick" | "disconnect">("");
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [keepSynced, setKeepSynced] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [removeContent, setRemoveContent] = useState(false);

  const fail = useCallback(
    (e: unknown) => {
      const code = e instanceof ApiError ? e.code : "";
      setError(KNOWN.includes(code) ? t(`drive.err.${code}` as never) : e instanceof ApiError && e.status === 403 ? t("drive.err.forbidden") : t("drive.err.other"));
    },
    [t],
  );

  // Back from Google: say how it went once, then clean the address.
  useEffect(() => {
    const r = readReturn(location.hash);
    if (!r || r.provider !== "google_drive") return;
    if (r.result === "connected") setNotice(t("drive.result.connected"));
    else setError(RESULTS.includes(r.result) ? t(`drive.result.${r.result}` as never) : t("drive.err.other"));
    history.replaceState(null, "", location.pathname + location.search + location.hash.split("?")[0]);
    void reload();
  }, [reload, t]);

  const conn = state.connection;
  const start = async () => {
    setBusy("connect");
    setError(null);
    try {
      const { url } = await api.connectIntegration("google_drive", location.hash.split("?")[0] || "#/");
      location.assign(url);
    } catch (e) {
      fail(e);
      setBusy("");
    }
  };
  const pick = async () => {
    setBusy("pick");
    setError(null);
    setNotice(null);
    try {
      const cfg = await api.pickerToken("google_drive");
      const files = await pickDriveFiles(cfg, lang);
      if (files.length) {
        await api.addSource(agentId, { type: "google_drive", files: files.slice(0, 100), keepSynced });
        setNotice(t("drive.added", { n: String(files.length) }));
        await refresh();
      }
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };
  const disconnect = async () => {
    setBusy("disconnect");
    setError(null);
    try {
      await api.disconnectIntegration("google_drive", removeContent);
      setConfirming(false);
      setNotice(removeContent ? t("drive.disconnected.removed") : t("drive.disconnected.kept"));
      await reload();
      await refresh();
    } catch (e) {
      fail(e);
    } finally {
      setBusy("");
    }
  };
  const syncNow = async (s: Source) => {
    setError(null);
    try {
      await api.reindexSource(agentId, s.id);
      await refresh();
    } catch (e) {
      fail(e);
    }
  };
  const remove = async (s: Source) => {
    try {
      await api.removeSource(agentId, s.id);
      await refresh();
    } catch (e) {
      fail(e);
    }
  };

  if (!state.available && !conn) return null;
  return (
    <Section title={t("drive.title")} help={t("drive.help")}>
      {notice && <p role="status" className="notice">{notice}</p>}
      {error && <p role="alert" className="field-error">{error}</p>}

      {!conn && (
        <>
          <p className="hint">{t("drive.privacy")}</p>
          <button className="btn primary" disabled={busy !== "" || !state.available} onClick={() => void start()}>
            {busy === "connect" ? t("drive.opening") : t("drive.connect")}
          </button>
        </>
      )}

      {conn && (
        <>
          <p>
            <Badge tone={conn.status === "active" ? "ok" : "bad"}>{conn.status === "active" ? t("drive.status.active") : t("drive.status.needsReconnect")}</Badge>{" "}
            {conn.email ? t("drive.connectedAs", { email: conn.email }) : null}
          </p>
          {conn.status === "needs_reconnect" ? (
            <div className="banner warn" role="alert">
              <p>{t("drive.needsReconnect")}</p>
              <button className="btn primary" disabled={busy !== ""} onClick={() => void start()}>{t("drive.connectAgain")}</button>
            </div>
          ) : (
            <>
              <label className="inline">
                <input type="checkbox" checked={keepSynced} onChange={(e) => setKeepSynced(e.target.checked)} /> {t("drive.keepSynced")}
              </label>
              <p className="hint">{t("drive.sheets")}</p>
              <button className="btn secondary" disabled={busy !== "" || state.held === true} onClick={() => void pick()}>
                {busy === "pick" ? t("drive.opening") : t("drive.choose")}
              </button>
            </>
          )}

          {sources.length > 0 && (
            <ul className="sources" aria-label={t("drive.sources")}>
              {sources.map((s) => (
                <DriveSource key={s.id} api={api} agentId={agentId} source={s} syncNow={syncNow} remove={remove} />
              ))}
            </ul>
          )}

          {!confirming ? (
            <button className="btn link" onClick={() => setConfirming(true)}>{t("drive.disconnect")}</button>
          ) : (
            <fieldset className="card" data-testid="drive-disconnect">
              <legend>{t("drive.disconnect.title")}</legend>
              <label className="inline"><input type="radio" name="drive-content" checked={!removeContent} onChange={() => setRemoveContent(false)} /> {t("drive.disconnect.keep")}</label>
              <label className="inline"><input type="radio" name="drive-content" checked={removeContent} onChange={() => setRemoveContent(true)} /> {t("drive.disconnect.remove")}</label>
              <div className="row">
                <button className="btn danger" disabled={busy !== ""} onClick={() => void disconnect()}>{t("drive.disconnect.confirm")}</button>
                <button className="btn secondary" onClick={() => setConfirming(false)}>{t("drive.cancel")}</button>
              </div>
            </fieldset>
          )}
        </>
      )}
    </Section>
  );
}

const FILE_TONE: Record<string, "ok" | "info" | "warn" | "bad" | "muted"> = { indexed: "ok", pending: "info", not_found: "warn", no_access: "warn", unsupported: "muted", failed: "bad" };

function DriveSource({ api, agentId, source, syncNow, remove }: { api: Api; agentId: string; source: Source; syncNow: (s: Source) => void; remove: (s: Source) => void }) {
  const { t } = useI18n();
  const [files, setFiles] = useState<DriveFiles | null>(null);
  const [open, setOpen] = useState(false);
  const toggle = async () => {
    setOpen(!open);
    if (!open) setFiles(await api.driveFiles(agentId, source.id).catch(() => null));
  };
  const last = source.sync?.lastRunAt ? new Date(source.sync.lastRunAt).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : null;
  return (
    <li data-status={source.status}>
      <div className="row between">
        <strong>{source.name}</strong>
        <Badge tone={source.status === "indexed" ? "ok" : source.status === "error" ? "bad" : "info"}>{t(`drive.source.${source.status}` as never)}</Badge>
      </div>
      {source.error && <p className="hint">{source.error}</p>}
      <p className="hint">{source.sync ? (last ? t("drive.lastSync", { when: last }) : t("drive.neverSynced")) : t("drive.importedOnce")}</p>
      <div className="row">
        <button className="btn secondary" onClick={() => syncNow(source)}>{t("drive.syncNow")}</button>
        <button className="btn secondary" aria-expanded={open} onClick={() => void toggle()}>{open ? t("drive.hideFiles") : t("drive.showFiles")}</button>
        <button className="btn link" aria-label={t("drive.removeSource", { name: source.name })} onClick={() => remove(source)}>{t("drive.remove")}</button>
      </div>
      {open && files && (
        <ul className="files" aria-label={t("drive.files")}>
          {files.files.map((f) => (
            <li key={f.id}>
              {f.name} <Badge tone={FILE_TONE[f.status] ?? "info"}>{t(`drive.file.${f.status}` as never)}</Badge>
              {f.note ? <span className="hint"> {f.note}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
